/**
 * 用户展示工具（架构 §8 共享知识；T02）
 *  - displayName：全站统一「昵称 (username)」消歧；无昵称则 username。
 *  - shortCode：#+id 的 base36，保证唯一可读。
 *  - avatarSrc：取头像 dataURL（空返回 null，由 UserAvatar 兜底）。
 *  - initialsBg：由 username 哈希派生稳定背景色（首字母头像兜底）。
 */
import type { PublicUser } from '../types/models';

/** 短码：#+id 的 base36，保证唯一可读（架构 §8） */
export function shortCode(user: { id: number }): string {
  return '#' + user.id.toString(36);
}

/**
 * 全站统一展示名：有昵称 -> "昵称 (username)"，无昵称 -> username。
 * 消歧靠 username 这一唯一身份键（架构 §5.1）。
 */
export function displayName(user: {
  username: string;
  nickname?: string | null;
}): string {
  const nick = user.nickname && user.nickname.trim();
  if (nick) return `${nick} (${user.username})`;
  return user.username;
}

/** 取头像 dataURL（avatar 为空返回 null，由 UserAvatar 兜底首字母） */
export function avatarSrc(user: { avatar?: string | null }): string | null {
  return user.avatar && user.avatar.trim() ? user.avatar : null;
}

/** 由 username 哈希派生稳定背景色（首字母头像兜底用） */
const AVATAR_BG_PALETTE = [
  '#22d3ee', '#a855f7', '#34d399', '#f97316', '#eab308',
  '#ec4899', '#3b82f6', '#14b8a6', '#8b5cf6', '#06b6d4',
];
export function initialsBg(username: string): string {
  let h = 0;
  for (let i = 0; i < username.length; i++) {
    h = (h * 31 + username.charCodeAt(i)) >>> 0;
  }
  return AVATAR_BG_PALETTE[h % AVATAR_BG_PALETTE.length];
}

/** 取首字（中文取首字，英文取首字母大写） */
export function initialsOf(username: string): string {
  const s = username.trim();
  if (!s) return '?';
  return s[0].toUpperCase();
}

/** 类型守卫：判断是否为可渲染头像的用户对象 */
export function isRenderableUser(
  u: { username: string; nickname?: string | null; avatar?: string | null } | null | undefined,
): u is PublicUser {
  return !!u && typeof u.username === 'string';
}
